(() => {
  const namespaceKey = 'GuruShopHomeFinal';
  const markerSelector = 'script[data-guru-home-final-root]';
  const existingApi = window[namespaceKey];

  if (existingApi?.scan) {
    existingApi.scan(document);
    return;
  }

  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();

  const mediaAllowed = (rawValue) => {
    if (typeof rawValue !== 'string' || !rawValue || rawValue !== rawValue.trim() || rawValue.length > 2048) return '';
    if (/[%\u0000-\u001f\u007f\\]/.test(rawValue)) return '';

    try {
      const url = new URL(rawValue, window.location.href);
      const sameOrigin = url.origin === window.location.origin;
      const shopifyAsset = url.protocol === 'https:' && url.hostname === 'cdn.shopify.com';
      const validTransport = sameOrigin || shopifyAsset;
      const validPath = /\/guru-home-final-[a-z0-9-]+\.(?:webp|png)$/i.test(url.pathname);

      const validVersion = !url.search || /^\?v=\d+$/.test(url.search);
      if (!validTransport || !validPath || !validVersion || url.username || url.password || url.port || url.hash) return '';
      return url.href;
    } catch (_error) {
      return '';
    }
  };

  function initRoot(root) {
    if (!root || initializedRoots.has(root)) return false;

    const abortController = new AbortController();
    const cleanups = [];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktopCategories = window.matchMedia('(min-width: 901px)');
    const { signal } = abortController;

    const hydrateDeferredRailMedia = (rail) => {
      const templates = [...rail.querySelectorAll('template')];
      for (const template of templates) template.replaceWith(template.content.cloneNode(true));
      rail.removeAttribute('data-home-final-deferred-media');
    };

    const deferredRails = [...root.querySelectorAll('[data-home-final-deferred-media]')];
    if (deferredRails.length > 0 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);
          hydrateDeferredRailMedia(entry.target);
        }
      }, { rootMargin: '300px 0px' });
      deferredRails.forEach((rail) => observer.observe(rail));
      cleanups.push(() => observer.disconnect());
    } else {
      deferredRails.forEach(hydrateDeferredRailMedia);
    }

    const updateRail = (rail) => {
      const track = rail.querySelector('[data-home-final-rail-track]');
      const previous = rail.querySelector('[data-home-final-rail-prev]');
      const next = rail.querySelector('[data-home-final-rail-next]');
      if (!track || !previous || !next) return;

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = getComputedStyle(track).direction === 'rtl'
        ? Math.abs(track.scrollLeft)
        : Math.max(0, track.scrollLeft);
      const hasOverflow = maxScroll > 2;

      previous.disabled = !hasOverflow || position <= 2;
      next.disabled = !hasOverflow || position >= maxScroll - 2;
      rail.toggleAttribute('data-home-final-rail-overflow', hasOverflow);
    };

    for (const rail of root.querySelectorAll('[data-home-final-rail]')) {
      const track = rail.querySelector('[data-home-final-rail-track]');
      const previous = rail.querySelector('[data-home-final-rail-prev]');
      const next = rail.querySelector('[data-home-final-rail-next]');
      if (!track || !previous || !next) continue;

      const move = (logicalDirection) => {
        const firstCard = track.firstElementChild;
        const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = (firstCard?.getBoundingClientRect().width || track.clientWidth) + gap;
        if (!Number.isFinite(step) || step <= 0) return;
        const rtlSign = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        track.scrollBy({
          left: logicalDirection * rtlSign * step,
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      };

      previous.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
      track.addEventListener('scroll', () => updateRail(rail), { passive: true, signal });
      track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => updateRail(rail));
        observer.observe(track);
        cleanups.push(() => observer.disconnect());
      }

      updateRail(rail);
    }

    for (const browser of root.querySelectorAll('[data-home-final-categories]')) {
      const currentBuffer = browser.querySelector('[data-home-final-category-current]');
      const nextBuffer = browser.querySelector('[data-home-final-category-next]');
      const links = [...browser.querySelectorAll('[data-home-final-category-link]')];
      if (!currentBuffer || !nextBuffer || links.length === 0) continue;

      let visibleImage = [currentBuffer, nextBuffer].find((image) => image.classList.contains('is-visible')) || currentBuffer;
      let standbyImage = visibleImage === currentBuffer ? nextBuffer : currentBuffer;
      let requestedSource = mediaAllowed(visibleImage.getAttribute('src') || '');

      const activate = async (link) => {
        if (!desktopCategories.matches || signal.aborted) return;
        const source = mediaAllowed(link.dataset.homeFinalCategoryImage);
        if (!source) return;
        const width = Number.parseInt(link.dataset.homeFinalCategoryWidth, 10);
        const height = Number.parseInt(link.dataset.homeFinalCategoryHeight, 10);
        if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || width > 4096 || height < 1 || height > 4096) return;
        const fit = link.dataset.homeFinalCategoryFit;
        if (fit !== 'cover' && fit !== 'contain') return;

        for (const candidate of links) candidate.classList.toggle('is-active', candidate === link);
        if (source === requestedSource) {
          visibleImage.width = width;
          visibleImage.height = height;
          visibleImage.classList.toggle('is-contain', fit === 'contain');
          return;
        }

        requestedSource = source;
        standbyImage.classList.remove('is-visible');
        standbyImage.classList.toggle('is-contain', fit === 'contain');
        standbyImage.width = width;
        standbyImage.height = height;
        standbyImage.alt = link.dataset.homeFinalCategoryAlt || '';
        standbyImage.removeAttribute('srcset');
        standbyImage.removeAttribute('sizes');
        standbyImage.src = source;
        try {
          await standbyImage.decode();
        } catch (_error) {
          // Keep the approved poster-like image swap when decoding is unavailable.
        }
        if (signal.aborted || source !== requestedSource) return;

        standbyImage.classList.add('is-visible');
        visibleImage.classList.remove('is-visible');
        visibleImage.alt = '';
        [visibleImage, standbyImage] = [standbyImage, visibleImage];
      };

      for (const link of links) {
        link.addEventListener('pointerenter', () => activate(link), { signal });
        link.addEventListener('focus', () => activate(link), { signal });
      }

      const syncCategory = () => {
        if (!desktopCategories.matches) return;
        activate(links.find((link) => link.classList.contains('is-active')) || links[0]);
      };
      desktopCategories.addEventListener('change', syncCategory, { signal });
      syncCategory();
    }

    root.setAttribute('data-home-final-enhanced', '');
    initializedRoots.add(root);
    cleanupByRoot.set(root, () => {
      abortController.abort();
      for (const cleanup of cleanups) cleanup();
      root.removeAttribute('data-home-final-enhanced');
      root.querySelectorAll('[data-home-final-rail-overflow]').forEach((rail) => rail.removeAttribute('data-home-final-rail-overflow'));
      initializedRoots.delete(root);
      cleanupByRoot.delete(root);
    });
    return true;
  }

  function scan(scope = document) {
    const markers = [];
    if (scope.matches?.(markerSelector)) markers.push(scope);
    markers.push(...scope.querySelectorAll?.(markerSelector) || []);

    for (const marker of markers) {
      const rootId = marker.dataset.guruHomeFinalRoot;
      if (!rootId || rootId.length > 160 || !/^[A-Za-z0-9_-]+$/.test(rootId)) continue;
      initRoot(document.getElementById(rootId));
    }

    const roots = [];
    if (scope.matches?.('[data-guru-home-final]')) roots.push(scope);
    roots.push(...scope.querySelectorAll?.('[data-guru-home-final]') || []);

    for (const root of roots) {
      const rootId = root.id;
      if (!rootId || rootId.length > 160 || !/^[A-Za-z0-9_-]+$/.test(rootId)) continue;
      const hasMarker = [...document.querySelectorAll(markerSelector)]
        .some((marker) => marker.dataset.guruHomeFinalRoot === rootId);
      if (hasMarker) initRoot(root);
    }
  }

  function cleanupWithin(scope) {
    if (!scope || scope.nodeType !== Node.ELEMENT_NODE) return;
    const roots = [];
    if (scope.matches('[data-guru-home-final]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-guru-home-final]'));
    for (const root of roots) cleanupByRoot.get(root)?.();
  }

  window[namespaceKey] = { scan };
  scan(document);

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupWithin(event.target));

  if ('MutationObserver' in window) {
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        }
        for (const node of record.removedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) cleanupWithin(node);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
